/**
 * Devices API — all backend calls for the Devices page.
 *
 * Nurse Call:  sp_dev_getInovonicsDevices        | device_unassign / device_delete
 * Wandering:   getaccutechdevices                | device_unassign / device_delete (shared)
 * System:      sp_dev_getrepeaterbycampus        | system_device_delete
 * Servers:     sp_dev_getPalcareDevicesByCampusID | server_add / server_delete
 */

import { apiFetch, apiTransaction, apiMqtt, apiQueryItems } from '../../../services/api.js';
import { getActiveVendorIdList } from '../../../services/session.js';
import { NURSE_CALL_CATEGORY_FILTER } from '../../../utils/deviceCategories.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/**
 * Fetch all Inovonics / PalCare devices for a campus.
 *
 * V1 procedure: sp_dev_getInovonicsDevices
 * Accepts payload with device_filter, device_category_filter, campus_id.
 */
/**
 * Fetch all Nurse Call (Inovonics / PalCare) devices for a campus.
 */
export function fetchDevices(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'sp_dev_getInovonicsDevices',
    reqType: 'stored',
    payload: {
      device_filter: '',
      device_category_filter: NURSE_CALL_CATEGORY_FILTER,
      campus_id: campusId,
    },
    readOnly,
  });
}

/**
 * Fetch device categories for the Add Device form.
 *
 * @param {number} campusId
 * @param {'area'|'resident'} type — Determines which stored procedure to call
 * @returns {Promise<Array>} — [{ device_category_id, device_category_short_name, ... }]
 */
export function fetchDeviceCategories(campusId, type = 'area') {
  const procedure = type === 'resident'
    ? 'getresidentdevicecategories'
    : 'getareadevicecategories';
  return apiFetch({
    procedure,
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/**
 * Fetch Inovonics device types with nested models[] and uses[].
 *
 * V1 procedure: getinovonicsdevicetypes
 *
 * @param {number} campusId
 * @returns {Promise<Array>} — Each entry has: inovonics_type_id, device_type_name,
 *   device_category_id, is_third_party, image_url, models[], uses[]
 */
export function fetchDeviceTypes(campusId) {
  return apiFetch({
    procedure: 'getinovonicsdevicetypes',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/**
 * Fetch PalCare device types with nested models[].
 *
 * V1 procedure: getpalcareresidentdevices
 *
 * @param {number} campusId
 * @returns {Promise<Array>} — Each entry has: device_type_id, device_type_name,
 *   device_category_id, models[]
 */
export function fetchPalcareDeviceTypes(campusId) {
  return apiFetch({
    procedure: 'getpalcareresidentdevices',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/**
 * Fetch the curated "easy learn-in" device shortlist.
 *
 * Backed by sp_getLearnInDevices, which resolves each curated row's display
 * name, image, category and model across the Inovonics and PalCare type tables.
 * This is what lets the simplified add flow collapse manufacturer / category /
 * type / model selection into a single pick.
 *
 * Rows: { cloud_id, manufacture_id, device_type_id, is_area, is_resident,
 *         device_type_name, image_url, device_category_id,
 *         device_category_short_name, sort_order, manufacture_name,
 *         manufacture_model_id?, manufacture_model_name? }
 *
 * @param {{ isArea?: number, isResident?: number }} scope
 * @returns {Promise<Array>} ordered by sort_order
 */
export function fetchLearnInDevices({ isArea = 0, isResident = 0 } = {}) {
  return apiFetch({
    procedure: 'getlearnindevices',
    reqType: 'stored',
    payload: { is_area: isArea, is_resident: isResident },
    readOnly: true,
  });
}

/**
 * Fetch the full advanced learn-in catalog (Nurse Call add-device flow).
 *
 * Backed by sp_getAdvLearnInDevices, which UNIONs the Inovonics, PalCare and
 * Becklar type tables and decides area-vs-resident itself, replacing the
 * category → type cascade the old drawer made the user click through.
 *
 * **Rows are not one-per-device.** The procedure LEFT JOINs the use and model
 * entity tables, so a type with 3 behaviors and 2 models arrives as 6 rows, and a
 * type with neither arrives once with null ids. Always run the result through
 * `foldCatalog()` in ../components/advLearnIn.js before use — and note that
 * folding must key on manufacture_id AND device_type_id, because the three UNION
 * branches read unrelated tables with independent id spaces.
 *
 * Takes no parameters: the catalog is campus-independent (mysql_fetch.py:1011).
 *
 * Rows: { device_type_id, device_use_id, is_area_device, is_resident_device,
 *         model_id, model_name, device_category_id, is_third_party,
 *         device_type_name, image_url, sound_id, option_number, option_text,
 *         message_class, product_type_id, device_category_short_name,
 *         manufacture_id, sort_order }
 *
 * @returns {Promise<Array>} raw, unfolded rows
 */
export function fetchAdvLearnInDevices() {
  return apiFetch({
    procedure: 'getadvlearnindevices',
    reqType: 'stored',
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Add a new Inovonics device (unassigned).
 *
 * V1 TransType: device_add_area (area devices) or device_add_resident (resident devices)
 *
 * @param {object} deviceData — Full device payload (campus_id, manufacture_uuid, etc.)
 * @param {string} transType — 'device_add_area' or 'device_add_resident'
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function addDevice(deviceData, transType, session) {
  const vendor_ids = getActiveVendorIdList();

  const payload = {
    ...deviceData,
    modified_by: session.userId,
    vendor_ids,
  };

  return apiTransaction({
    transType,
    payload: [payload],
    oldPayload: [],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Unassign (disable) one or more devices.
 *
 * Sets is_active = false, clears resident_id and area_id.
 * V1 TransType: device_unassign
 *
 * @param {Array<object>} devices — array of { device_id, area_id, resident_id }
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function unassignDevices(devices, session, grandstream = false) {
  const vendor_ids = getActiveVendorIdList();

  const payload = devices.map((d) => ({
    device_id: d.device_id,
    device_name: d.device_name ?? null,
    manufacture_id: d.manufacture_id ?? null,
    sip_extension: d.sip_extension ?? null,
    manufacture_uuid: d.manufacture_uuid ?? "",
    is_grandstream: grandstream,
    is_active: false,
    resident_id: null,
    area_id: null,
    modified_by: session.userId,
    vendor_ids,
  }));

  const oldPayload = devices.map((d) => ({
    device_id: d.device_id,
    area_id: d.area_id ?? null,
    resident_id: d.resident_id ?? null,
    sip_extension: d.sip_extension ?? null
  }));

  return apiTransaction({
    transType: 'device_unassign',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Delete one or more devices permanently.
 *
 * V1 TransType: device_delete
 *
 * @param {Array<object>} devices — array of { device_id, area_id, resident_id }
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function deleteDevices(devices, session, grandstream = false) {
  const vendor_ids = getActiveVendorIdList();

  const payload = devices.map((d) => ({
    device_id: d.device_id,
    modified_by: session.userId,
    is_grandstream: grandstream,
    sip_extension: d.sip_extension ?? null,
    manufacture_id: d.manufacture_id ?? null,
    vendor_ids,
  }));

  const oldPayload = devices.map((d) => ({
    device_id: d.device_id,
    area_id: d.area_id ?? null,
    resident_id: d.resident_id ?? null,
    device_type_id: d.device_type_id ?? null,
    device_category_id: parseInt(d?.device_category_id ? d.device_category_id : 0) ?? null,
    manufacture_id: d.manufacture_id ?? null,
    sip_extension: d.sip_extension ?? null,
  }));

  return apiTransaction({
    transType: 'device_delete',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

// ---------------------------------------------------------------------------
// Wandering (Accutech) devices
// ---------------------------------------------------------------------------

/**
 * Fetch all Accutech / wandering devices for a campus.
 *
 * V1 procedure: getaccutechdevices
 */
export function fetchWanderingDevices(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'getaccutechdevices',
    reqType: 'stored',
    payload: { device_filter: '', campus_id: campusId },
    readOnly,
  });
}

/**
 * Fetch Accutech (RF Tag) device type definitions for the Add Device form.
 *
 * V1 procedure: getaccutechresidentdevices
 *
 * @param {number} campusId
 * @returns {Promise<Array>} — Each entry has: accutech_device_id, accutech_model_id, description
 */
export function fetchAccutechDeviceTypes(campusId) {
  return apiFetch({
    procedure: 'getaccutechresidentdevices',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

// Wandering devices share the same core_device table as Nurse Call.
// Use unassignDevices() and deleteDevices() above for write operations.

// ---------------------------------------------------------------------------
// SIP / Speaker devices (Grandstream)
// ---------------------------------------------------------------------------

/**
 * Fetch all SIP / speaker devices for a campus.
 *
 * V1 procedure: getsipdevices
 */
export function fetchSpeakerDevices(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'getsipdevices',
    reqType: 'stored',
    payload: { device_filter: '', campus_id: campusId },
    readOnly,
  });
}

/**
 * Fetch SIP device type definitions for the Add Speaker Device form.
 *
 * V1 procedure: getsipdevicetypes — global lookup, no parameters.
 *
 * @returns {Promise<Array>} — Each entry: { device_type_id, device_type_name, device_category_id }
 */
export function fetchSpeakerDeviceTypes() {
  return apiFetch({
    procedure: 'getsipdevicetypes',
    reqType: 'stored',
    readOnly: true,
  });
}

/**
 * Fetch SIP device model definitions for the Add Speaker Device form.
 *
 * V1 procedure: getsipdevicemodels — global lookup, no parameters.
 *
 * @returns {Promise<Array>} — Each entry: { sip_device_id, model_name, description, is_grandstream }
 */
export function fetchSpeakerDeviceModels() {
  return apiFetch({
    procedure: 'getsipdevicemodels',
    reqType: 'stored',
    readOnly: true,
  });
}

/**
 * Add a new SIP / speaker device (unassigned).
 *
 * V1 TransType: sip_device_add (stub — backend not yet implemented).
 * Server category (device_category_id === 14) routes to sip_system_device_add.
 * Payload mirrors the Nurse Call addDevice shape.
 *
 * @param {object} deviceData — Full device payload
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function addSpeakerDevice(deviceData, session) {
  const vendor_ids = getActiveVendorIdList();

  const payload = {
    ...deviceData,
    modified_by: session.userId,
    vendor_ids,
  };

  const transType = Number(deviceData?.device_category_id) === 14
    ? 'sip_system_device_add'
    : 'sip_device_add';

  return apiTransaction({
    transType,
    payload: [payload],
    oldPayload: [],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

// Speaker devices share the same core_device table as Nurse Call.
// Use unassignDevices() and deleteDevices() above for write operations.

// ---------------------------------------------------------------------------
// System devices (Repeaters)
// ---------------------------------------------------------------------------

/**
 * Fetch all system devices (repeaters) for a campus.
 *
 * V1 procedure: sp_dev_getrepeaterbycampus
 */
export function fetchSystemDevices(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'sp_dev_getrepeaterbycampus',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly,
  });
}

/**
 * Delete one or more system devices permanently.
 *
 * V1 TransType: system_device_delete
 *
 * @param {Array<object>} devices — array of { device_id }
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function deleteSystemDevices(devices, session) {
  const vendor_ids = getActiveVendorIdList();

  const payload = devices.map((d) => ({
    device_id: d.device_id,
    device_type_id: d.device_type_id ?? null,
    manufacture_id: d.manufacture_id ?? null,
    vendor_ids,
  }));

  return apiTransaction({
    transType: 'system_device_delete',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

// ---------------------------------------------------------------------------
// Server devices (PalCare servers)
// ---------------------------------------------------------------------------

/**
 * Fetch all PalCare server devices for a campus.
 *
 * V1 procedure: sp_dev_getPalcareDevicesByCampusID
 */
export function fetchServers(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'sp_dev_getPalcareDevicesByCampusID',
    reqType: 'stored',
    payload: { device_filter: '', campus_id: campusId },
    readOnly,
  });
}

/**
 * Fetch areas for the Add Server form dropdown.
 *
 * V1 procedure: sp_dev_getareasbycampusid
 */
export function fetchServerAreas(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getareasbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/**
 * Add a new server device.
 *
 * V1 TransType: server_add
 *
 * @param {object} serverData — see payload shape in plan
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function addServer(serverData, session) {
  return apiTransaction({
    transType: 'server_add',
    payload: [serverData],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Delete (disable) one or more server devices.
 *
 * V1 TransType: server_delete
 *
 * @param {Array<object>} servers — array of { palcare_device_id, mac_addr }
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function deleteServers(servers, session) {
  const payload = servers.map((s) => ({
    palcare_device_id: s.palcare_device_id,
    campus_id: session.campusId,
    mac_addr: s.mac_addr,
  }));

  return apiTransaction({
    transType: 'server_delete',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

// ---------------------------------------------------------------------------
// Device Learn-In (Listen)
// ---------------------------------------------------------------------------

/**
 * Publish a learn-in MQTT command to start listening for Inovonics devices.
 *
 * V1 reference: Areas.jsx publishListen() — Operation: "learnin"
 *
 * @param {object}          options
 * @param {number}          options.campusId
 * @param {string}          options.clientId     — Session ID (used as LearnIn partition key)
 * @param {number|number[]} options.productType  — Product type code(s) to listen for
 * @param {number|string}   options.msgClass     — Message class for the device type
 * @returns {Promise<[boolean, string?]>}
 */
export function publishLearnIn({ campusId, clientId, productType, msgClass }) {
  return apiMqtt({
    operation: 'learnin',
    clientId,
    campusId,
    productType,
    msgClass: String(msgClass),
  });
}

/**
 * Poll the LearnIn DynamoDB table for devices heard during an active listen.
 *
 * V1 reference: Areas.jsx listenCount useEffect — Table: "LearnIn", PKey: "ClientId"
 *
 * Each result record has: { trx_id, pType, name }
 *   - trx_id  = device serial / transmitter ID
 *   - pType   = product type ID
 *   - name    = device display name
 *
 * @param {object} options
 * @param {number} options.campusId
 * @param {string} options.clientId — Session ID
 * @returns {Promise<Array>}
 */
export function pollLearnInDevices({ campusId, clientId }) {
  return apiQueryItems({
    table: 'LearnIn',
    partitionKey: 'ClientId',
    partitionValue: clientId,
    campusId,
  });
}
